import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { leaderboard as leaderboardApi } from '../../services/api';
import type { LeaderboardRow, MetricDirection, Submission } from '../../types/leaderboard';
import {
  deltaToneClass,
  formatDuration,
  formatScore,
  formatTestScoreSpread,
  isSubmissionActive,
  sampleCoverage,
  submissionStatusMeta,
  totalErrors,
} from '../../utils/leaderboardSpec';
import UsageBreakdown from '../UsageBreakdown';
import {
  type CaseScores,
  type CaseUsage,
  meanAcrossCases,
  parseScoresByCase,
  parseUsageByCaseTotals,
  splitDelta,
} from '../../utils/scoresByCase';

/**
 * История отправок одного алгоритма (AIT-116).
 *
 * Открывается по счётчику в строке таблицы. Лучшая отправка — та, что видна в
 * таблице, — помечена явно, иначе неясно, откуда взялся счёт в строке.
 */

interface Props {
  leaderboardId: string;
  row: LeaderboardRow;
  /** `dataset_id` → имя из каталога интеграции. Сабмишен несёт только
   *  `case_id` вида `current:<uuid>`, а по uuid не понять, какой датасет
   *  просел. Имени нет — показываем идентификатор, а не выдумываем подпись. */
  datasetNames?: Record<string, string>;
  /** Направление первичной метрики табло: без него дельту нельзя окрасить. */
  direction?: MetricDirection;
  onClose: () => void;
}

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const meta = submissionStatusMeta(status);
  return (
    <span className={`lb-pill lb-pill--${meta.tone}`} title={meta.hint}>
      {meta.label}
    </span>
  );
};

/** Пути к артефактам — текст, а не ссылка: ML пока складывает их у себя локально. */
const ArtifactPath: React.FC<{ label: string; uri?: string | null }> = ({ label, uri }) =>
  uri ? (
    <div className="lb-artifact">
      <span className="lb-artifact__label">{label}</span>
      <code className="lb-artifact__value" title="A path inside the ML container for now, not a link">
        {uri}
      </code>
    </div>
  ) : null;


const formatCaseScore = (value: number | null) =>
  value === null ? <span className="lb-muted">—</span> : value.toFixed(3);

const formatTokens = (value: number | null) =>
  value === null ? <span className="lb-muted">—</span> : Math.round(value).toLocaleString();

const formatCoefficient = (value: number | null) =>
  value === null ? <span className="lb-muted">—</span> : value.toFixed(2);

/** Разница «после минус до». Знак показывается всегда: без него плюс ноль
 *  ноль два и минус ноль ноль два выглядят одинаково важными. Цвет берётся из
 *  направления метрики — на `minimize` падение это улучшение, а не минус. */
const formatDelta = (value: number | null, direction?: MetricDirection) => {
  if (value === null) return <span className="lb-muted">—</span>;
  const text = `${value >= 0 ? '+' : ''}${value.toFixed(3)}`;
  return <span className={deltaToneClass(value, direction) || undefined}>{text}</span>;
};

/** Скоры по каждому датасету, без агрегации.
 *
 *  В таблице виден только ранжирующий скор — среднее без весов по датасетам, —
 *  и по нему не понять, какой датасет тянет вниз. Разброс между датасетами
 *  внутри одного сабмишена бывает больше, чем разброс между алгоритмами, и
 *  тогда сравнивать алгоритмы по агрегату попросту не о чем.
 *
 *  Строка среднего показана рядом нарочно: это то самое число, что стоит в
 *  таблице, и видно, из чего оно сложилось. */
const ScoresByCase: React.FC<{
  raw: unknown;
  usageByCase: unknown;
  datasetTokens: unknown;
  datasetNames?: Record<string, string>;
}> = ({ raw, usageByCase, datasetTokens, datasetNames }) => {
  const cases: CaseScores[] = React.useMemo(() => parseScoresByCase(raw), [raw]);
  const usage: Record<string, CaseUsage> = React.useMemo(
    () => parseUsageByCaseTotals(usageByCase, datasetTokens),
    [usageByCase, datasetTokens],
  );
  if (!cases.length) return null;

  return (
    <div className="lb-cases">
      <span className="lb-cases__title">Scores by dataset</span>
      <table className="lb-cases__table">
        <thead>
          <tr>
            <th scope="col">Dataset</th>
            <th scope="col">Train</th>
            <th scope="col">Validation</th>
            <th scope="col">Test</th>
            <th scope="col" title="Tokens in the dataset: train + validation + test">Tokens</th>
            <th scope="col" title="Spent on this dataset / its size">Coefficient</th>
          </tr>
        </thead>
        <tbody>
          {cases.map((item) => (
            <tr key={item.caseId}>
              <th scope="row" title={item.caseId}>
                {datasetNames?.[item.label] ?? item.label}
                {item.source ? <span className="lb-cases__source">{item.source}</span> : null}
              </th>
              <td>{formatCaseScore(item.train)}</td>
              <td>{formatCaseScore(item.validation)}</td>
              <td>{formatCaseScore(item.test)}</td>
              <td>{formatTokens(usage[item.caseId]?.datasetTokens ?? null)}</td>
              <td>{formatCoefficient(usage[item.caseId]?.coefficient ?? null)}</td>
            </tr>
          ))}
          {cases.length > 1 ? (
            <tr className="lb-cases__mean">
              <th scope="row">Unweighted mean</th>
              <td>{formatCaseScore(meanAcrossCases(cases, 'train'))}</td>
              <td>{formatCaseScore(meanAcrossCases(cases, 'validation'))}</td>
              <td>{formatCaseScore(meanAcrossCases(cases, 'test'))}</td>
              <td colSpan={2} className="lb-cases__note">
                the algorithm and the cycle around it are not split per dataset — see the total below
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
};

const SubmissionCard: React.FC<{
  item: Submission;
  isBest: boolean;
  datasetNames?: Record<string, string>;
  direction?: MetricDirection;
}> = ({ item, isBest, datasetNames, direction }) => {
  const [open, setOpen] = useState(false);
  const coverage = sampleCoverage(item.sample_stats);
  const errors = totalErrors(item.error_stats);

  return (
    <article className={`lb-sub ${isBest ? 'lb-sub--best' : ''}`}>
      <header className="lb-sub__head">
        <StatusPill status={item.status} />
        {isBest ? (
          <span
            className="lb-badge lb-badge--best"
            title="The row picks its best run by validation score — this is that run, shown here with its test result, which need not be the best test score of the bunch"
          >
            Picked by validation
          </span>
        ) : null}
        <span className="lb-sub__score">{formatScore(item.primary_score)}</span>
        <span className="lb-sub__when">{item.created_at ? new Date(item.created_at).toLocaleString() : '—'}</span>
        <button type="button" className="lb-btn lb-btn--xs" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide details' : 'Details'}
        </button>
      </header>

      <dl className="lb-facts">
        <div className="lb-fact">
          <dt>Validation</dt>
          <dd>{formatScore(item.validation_score)}</dd>
        </div>
        <div className="lb-fact lb-fact--wide">
          <dt>Test before → after</dt>
          <dd>
            {formatScore(item.test_initial_score)} → {formatScore(item.test_final_score)}
          </dd>
        </div>
        <div className="lb-fact">
          <dt>Train delta</dt>
          <dd>{formatDelta(splitDelta(item.metrics?.train, item.metrics?.train_initial), direction)}</dd>
        </div>
        <div className="lb-fact">
          <dt>Validation delta</dt>
          <dd>{formatDelta(splitDelta(item.metrics?.validation, item.metrics?.validation_initial), direction)}</dd>
        </div>
        <div className="lb-fact">
          <dt>Test delta</dt>
          <dd className={deltaToneClass(item.test_delta, direction) || undefined}>
            {formatScore(item.test_delta)}
          </dd>
        </div>
        <div className="lb-fact">
          <dt>Iterations</dt>
          <dd>{item.iterations_done ?? '—'}</dd>
        </div>
        <div className="lb-fact">
          <dt>Duration</dt>
          <dd>{formatDuration(item.duration_s)}</dd>
        </div>
      </dl>

      <ScoresByCase
        raw={item.scores_by_case}
        usageByCase={item.usage_by_case}
        datasetTokens={item.dataset_tokens}
        datasetNames={datasetNames}
      />

      <UsageBreakdown
        usage={item.usage_totals}
        usageByCase={item.usage_by_case}
        usageStatus={item.usage_status}
        costStatus={item.cost_status}
        datasetNames={datasetNames}
        emptyText="No usage was reported for this submission."
      />

      {/* Счёт без покрытия ничего не значит, поэтому строка обязательная. */}
      <div className="lb-sub__quality">
        {coverage ? (
          <span className={`lb-cov ${coverage.thin ? 'lb-cov--thin' : ''}`}>
            Scored on {coverage.scored} of {coverage.total} samples
            {coverage.thin ? ' — read the score with that in mind' : ''}
          </span>
        ) : (
          <span className="lb-muted">No sample stats reported</span>
        )}
        {errors > 0 ? (
          <span className="lb-errs">
            {item.error_stats?.algorithm_errors ? `${item.error_stats.algorithm_errors} algorithm` : ''}
            {item.error_stats?.evaluator_errors ? `${item.error_stats.algorithm_errors ? ', ' : ''}${item.error_stats.evaluator_errors} evaluator` : ''}
            {item.error_stats?.timeouts ? `${errors - (item.error_stats.timeouts ?? 0) > 0 ? ', ' : ''}${item.error_stats.timeouts} timeout` : ''}
            {' errors'}
          </span>
        ) : null}
      </div>

      {item.error ? (
        <p className="lb-sub__error">
          {item.error_type ? <strong>{item.error_type}: </strong> : null}
          {item.error}
        </p>
      ) : null}
      {item.cancel_reason ? <p className="lb-muted">Cancelled: {item.cancel_reason}</p> : null}

      {open ? (
        <div className="lb-sub__details">
          <ArtifactPath label="Final config" uri={item.final_config_uri} />
          <ArtifactPath label="Logs" uri={item.logs_uri} />
          {!item.final_config_uri && !item.logs_uri ? <span className="lb-muted">No artifacts reported.</span> : null}
        </div>
      ) : null}
    </article>
  );
};

const LeaderboardSubmissionsModal: React.FC<Props> = ({ leaderboardId, row, datasetNames, direction, onClose }) => {
  const [items, setItems] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await leaderboardApi.listSubmissions(leaderboardId, row.algorithm.id, { limit: 50 });
      setItems(res.data?.items ?? []);
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(err.response?.data?.error?.message || 'Could not load the submission history.');
    } finally {
      setLoading(false);
    }
  }, [leaderboardId, row.algorithm.id]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  useEffect(() => {
    void load();
  }, [load]);

  // Пока что-то считается — подтягиваем свежее состояние.
  useEffect(() => {
    if (!items.some((i) => isSubmissionActive(i.status))) return undefined;
    const t = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(t);
  }, [items, load]);

  const bestId = row.best_submission?.id;

  // Выбранный по валидации прогон — первым: строка таблицы показывает именно
  // его тестовый результат, и должно быть видно, откуда он взялся.
  const orderedItems = useMemo(() => {
    if (!bestId) return items;
    const best = items.find((i) => i.id === bestId);
    if (!best) return items;
    return [best, ...items.filter((i) => i.id !== bestId)];
  }, [items, bestId]);

  const cancel = async (item: Submission) => {
    setCancellingId(item.id);
    setError(null);
    try {
      await leaderboardApi.cancelSubmission(item.id);
      await load();
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(err.response?.data?.error?.message || 'Could not cancel the submission.');
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="lb-modal-overlay" role="dialog" aria-modal="true" aria-label="Submission history" onClick={onClose}>
      <div className="lb-modal" onClick={(e) => e.stopPropagation()}>
        <header className="lb-modal__head">
          <div className="lb-modal__title">
            <h3>{row.algorithm.name}</h3>
            <code className="lb-modal__id">{row.algorithm.algorithm_id}</code>
          </div>
          <button type="button" className="lb-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        {typeof row.succeeded_count === 'number' || typeof row.test_score_mean === 'number' ? (
          <div className="lb-modal__summary">
            <span><strong>{row.succeeded_count ?? '—'}</strong> succeeded</span>
            <span>Test {formatTestScoreSpread(row.test_score_mean, row.test_score_stddev, row.succeeded_count)}</span>
            {typeof row.test_score_min === 'number' && typeof row.test_score_max === 'number' ? (
              <span>
                Range {formatScore(row.test_score_min)} – {formatScore(row.test_score_max)}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="lb-modal__body" data-testid="lb-submissions">
          {loading && items.length === 0 ? (
            <p className="lb-muted">Loading history…</p>
          ) : error ? (
            <div className="lb-banner lb-banner--danger">{error}</div>
          ) : items.length === 0 ? (
            <p className="lb-muted">This algorithm has no submissions yet.</p>
          ) : (
            <div className="lb-subs">
              {orderedItems.map((item) => (
                <div key={item.id}>
                  <SubmissionCard item={item} isBest={!!bestId && item.id === bestId} datasetNames={datasetNames} direction={direction} />
                  {isSubmissionActive(item.status) ? (
                    <button type="button" className="lb-btn lb-btn--xs lb-btn--danger lb-sub-cancel" disabled={cancellingId === item.id} onClick={() => void cancel(item)}>
                      {cancellingId === item.id ? 'Cancelling…' : 'Cancel queued work'}
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        <footer className="lb-modal__foot">
          <span className="lb-muted">{row.submissions_count} submissions total</span>
          <button type="button" className="lb-btn" onClick={() => void load()} disabled={loading}>
            Refresh
          </button>
        </footer>
      </div>
    </div>
  );
};

export default LeaderboardSubmissionsModal;
